/**
 * TheirWorldLauncher — a card on PersonPage that lists the capsules that
 * apply to this person and launches the immersive takeover view.
 *
 * Silent when no capsules match (person too modern, too fragmentary, or
 * outside the curated geography). Which is fine — this is an invitation
 * when available, not a demand on the layout.
 */
import { useEffect, useState } from 'react'
import { api } from '../../services/api.js'
import TheirWorldView from './TheirWorldView.jsx'

const THEME_ICON = {
  war:       '⚔️',
  childhood: '🧒',
  work:      '🔨',
  migration: '🧳',
  epidemic:  '🦠',
  flood:     '🌊',
  other:     '🌍',
}

// Themed gradient fallback so a missing image still has visual presence
// instead of a flat black rectangle. Each theme gets its own mood.
const THEME_GRADIENT = {
  war:       'linear-gradient(135deg, #3a2520 0%, #6b3c28 100%)',
  childhood: 'linear-gradient(135deg, #2d4a3e 0%, #5a7d65 100%)',
  work:      'linear-gradient(135deg, #2a2a2a 0%, #4a3a2a 100%)',
  migration: 'linear-gradient(135deg, #1e3a5f 0%, #3d5a7f 100%)',
  epidemic:  'linear-gradient(135deg, #2d2a3a 0%, #4a4560 100%)',
  flood:     'linear-gradient(135deg, #1a3a5a 0%, #3a6080 100%)',
  other:     'linear-gradient(135deg, #2a2a2a 0%, #4a4a4a 100%)',
}

export default function TheirWorldLauncher({ personId, personName }) {
  const [capsules, setCapsules] = useState(null)
  const [openSlug, setOpenSlug] = useState(null)

  useEffect(() => {
    if (!personId) return
    api.get(`/persons/${personId}/capsules`)
      .then(setCapsules)
      .catch(() => setCapsules([]))
  }, [personId])

  if (!capsules || capsules.length === 0) return null

  return (
    <>
      <div className="their-world-launcher">
        <div className="their-world-launcher__eyebrow">Their world</div>
        <div className="their-world-launcher__lead">
          {personName?.split(' ')[0] || 'This person'} lived through periods we've
          reconstructed. Step into one.
        </div>
        <div className="their-world-launcher__cards">
          {capsules.map(c => (
            <CapsuleCard
              key={c.id}
              capsule={c}
              onOpen={() => setOpenSlug(c.slug)}
            />
          ))}
        </div>
      </div>

      {openSlug && (
        <TheirWorldView
          personId={personId}
          slug={openSlug}
          onClose={() => setOpenSlug(null)}
        />
      )}
    </>
  )
}

// Card uses a real <img> as the background so we can detect load failure
// and swap to a themed gradient. CSS background-image can't do that.
// The overlay gradient keeps the text readable over any image / gradient.
function CapsuleCard({ capsule, onOpen }) {
  const [imgOk, setImgOk] = useState(true)
  const gradient = THEME_GRADIENT[capsule.theme] || THEME_GRADIENT.other
  const scopeLabel =
    capsule.match?.geo === 'place'   ? capsule.place :
    capsule.match?.geo === 'region'  ? capsule.region :
    capsule.match?.geo === 'country' ? 'Netherlands' : ''

  return (
    <button className="their-world-card" onClick={onOpen}>
      {capsule.hero_image_url && imgOk && (
        <img
          className="their-world-card__bg"
          src={capsule.hero_image_url}
          alt=""
          aria-hidden
          loading="lazy"
          onError={() => setImgOk(false)}
        />
      )}
      {(!capsule.hero_image_url || !imgOk) && (
        <div
          className="their-world-card__bg their-world-card__bg--fallback"
          style={{ background: gradient }}
          aria-hidden
        />
      )}
      <div className="their-world-card__overlay" />
      <div className="their-world-card__body">
        <div className="their-world-card__icon">{THEME_ICON[capsule.theme] || '🌍'}</div>
        <div className="their-world-card__title">{capsule.title}</div>
        {capsule.tagline && <div className="their-world-card__tagline">{capsule.tagline}</div>}
        <div className="their-world-card__meta">
          {capsule.start_year}{capsule.end_year !== capsule.start_year && `–${capsule.end_year}`}
          {scopeLabel && ` · ${scopeLabel}`}
        </div>
      </div>
    </button>
  )
}
